import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import moment from "moment";
import {
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { ToastContainer, toast } from "react-toastify";
import Pagination from "../../component/Pagination";
import { fetchVotePurchases } from "../../api/votePurchaseAdmin";

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "completed", label: "Completed" },
  { value: "pending", label: "Pending" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
];

const columns = [
  { id: "date", label: "DATE" },
  { id: "user", label: "USER" },
  { id: "competition", label: "COMPETITION" },
  { id: "votes", label: "VOTES" },
  { id: "amount", label: "AMOUNT" },
  { id: "status", label: "STATUS" },
];

function statusChipProps(status) {
  switch (status) {
    case "completed":
      return { label: "Completed", sx: { bgcolor: "#e8f5e9", color: "#2e7d32" } };
    case "pending":
      return { label: "Pending", sx: { bgcolor: "#fff8e1", color: "#f57c00" } };
    case "failed":
      return { label: "Failed", sx: { bgcolor: "#ffebee", color: "#c62828" } };
    case "cancelled":
      return { label: "Cancelled", sx: { bgcolor: "#f3f4f6", color: "#6b7280" } };
    default:
      return { label: status || "—", sx: { bgcolor: "#f3f4f6", color: "#6b7280" } };
  }
}

function formatMoney(amount, currency) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: (currency || "GBP").toUpperCase(),
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${(currency || "GBP").toUpperCase()}`;
  }
}

function SummaryCard({ label, value, sub }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid #e5e7eb",
        flex: "1 1 180px",
        minWidth: 160,
      }}
    >
      <Typography sx={{ color: "#6b7280", fontSize: 12, fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 800, mt: 0.5, color: "#111827" }}>
        {value}
      </Typography>
      {sub ? (
        <Typography sx={{ color: "#9ca3af", fontSize: 11, mt: 0.35 }}>
          {sub}
        </Typography>
      ) : null}
    </Paper>
  );
}

export default function VotePurchases() {
  const token = useSelector((state) => state.auth.token);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [competitionFilter, setCompetitionFilter] = useState("");
  const [competitions, setCompetitions] = useState([]);

  const loadPurchases = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const res = await fetchVotePurchases(token, {
          page,
          search,
          status: statusFilter,
          competitionId: competitionFilter,
        });
        if (res.success) {
          setRows(res.data?.rows || []);
          setSummary(res.data?.summary || null);
          setCurrentPage(res.data?.currentPage || page);
          setTotalPages(res.data?.totalPages || 0);
        } else {
          toast.error(res.message || "Failed to load payments");
        }
      } catch (e) {
        toast.error(e?.response?.data?.message || "Failed to load payments");
      } finally {
        setLoading(false);
      }
    },
    [token, search, statusFilter, competitionFilter],
  );

  useEffect(() => {
    async function loadCompetitions() {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/getAllCompetitions?page=1&limit=200`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.data?.success) {
          setCompetitions(res.data?.data?.rows || res.data?.data || []);
        }
      } catch {
        // non-blocking
      }
    }
    loadCompetitions();
  }, [token]);

  useEffect(() => {
    loadPurchases(1);
  }, [loadPurchases]);

  const handleSearchSubmit = () => loadPurchases(1);

  const completedRevenue = summary
    ? formatMoney(summary.completedAmount, "GBP")
    : "—";

  return (
    <>
      <ToastContainer />
      <Box className="page_title">
        <Typography variant="h1" component="h1">
          Vote Payments
        </Typography>
        <Typography sx={{ color: "#6b7280", fontSize: 13, mt: 0.5 }}>
          Payment history for competition vote pack purchases
        </Typography>
      </Box>

      <Box className="page_inner">
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            mb: 2.5,
          }}
        >
          <SummaryCard
            label="Completed payments"
            value={summary?.completedCount ?? "—"}
          />
          <SummaryCard
            label="Completed revenue"
            value={completedRevenue}
            sub="Sum of completed transactions in list filters"
          />
          <SummaryCard
            label="Pending"
            value={summary?.pendingCount ?? "—"}
          />
          <SummaryCard
            label="Failed / cancelled"
            value={
              summary
                ? (summary.failedCount || 0) + (summary.cancelledCount || 0)
                : "—"
            }
          />
        </Box>

        <Box
          className="search_box_wrapper"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 2,
            flexWrap: "wrap",
            mb: 2,
            width: "100%",
          }}
        >
          <Box className="search_box" sx={{ width: { xs: "100%", md: "40%" } }}>
            <Box className="input-wrapper">
              <SearchIcon
                className="searchIcon"
                onClick={handleSearchSubmit}
              />
              <input
                placeholder="Search by user name or email"
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearchSubmit();
                }}
              />
            </Box>
          </Box>
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160, bgcolor: "#fff" }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Competition"
            value={competitionFilter}
            onChange={(e) => setCompetitionFilter(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 220, bgcolor: "#fff" }}
          >
            <MenuItem value="">All competitions</MenuItem>
            {competitions.map((c) => (
              <MenuItem key={c.id} value={String(c.id)}>
                {c.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box className="page_table tournament">
          <TableContainer
            component={Paper}
            className="customer_form user_table"
            sx={{ overflowX: "auto" }}
          >
            <Table style={{ width: "100%", minWidth: "960px" }}>
              <TableHead className="table_Head">
                <TableRow>
                  {columns.map((col) => (
                    <TableCell key={col.id}>{col.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center" sx={{ py: 6 }}>
                      <CircularProgress sx={{ color: "#ef364e" }} />
                    </TableCell>
                  </TableRow>
                ) : rows.length > 0 ? (
                  rows.map((row) => {
                    const chip = statusChipProps(row.status);
                    return (
                      <TableRow key={row.id}>
                        <TableCell>
                          {moment(row.createdAt).format("MMM D, YYYY HH:mm")}
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                            {row.user_name}
                          </Typography>
                          <Typography sx={{ color: "#6b7280", fontSize: 12 }}>
                            {row.user_email}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.competition_name}</TableCell>
                        <TableCell>{row.votes}</TableCell>
                        <TableCell>
                          {formatMoney(row.amount, row.currency)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={chip.label}
                            sx={{ fontWeight: 600, ...chip.sx }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center" sx={{ py: 4 }}>
                      No payment records found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 ? (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              handleChangePage={(_, page) => loadPurchases(page)}
            />
          ) : null}
        </Box>
      </Box>
    </>
  );
}
